import React, { useEffect, useMemo, useState } from 'react'
import SummaryIndicator from './SummaryIndicator'
import { Button, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { range, compact } from 'lodash'
import { blackColor, grey, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { useDispatch } from 'react-redux'
import AgriAction from 'agriAdministration/actions/AgriAction'
import { useParams } from 'react-router'
import VPIndicator from './VPIndicator'
import SmallIndicator from './SmallIndicator'
import { CheckCircleOutline, ErrorOutline, HelpOutline } from '@mui/icons-material'
import { getResourceVPLevel, getVPLevelLabel } from 'territory/utils/A3PUtils'
import { hasValue } from 'utils/NumberUtil'
import { linkStationDetailsShape } from 'territory/shapes/A3PShapes'
import { DEPTH } from 'utils/PiezometryUtils'

const StatusLine = ({ isValid, validLabel, invalidLabel, unknownLabel }) => {
    const getIcon = () => {
        if (!hasValue(isValid)) return <HelpOutline sx={{ color: grey }} />
        if (isValid) return <CheckCircleOutline sx={{ color: PASTEL_COLOR.green }} />
        return <ErrorOutline sx={{ color: PASTEL_COLOR.red }} />
    }
    const getLabel = () => {
        if (!hasValue(isValid)) return unknownLabel
        return isValid ? validLabel : invalidLabel
    }
    return (
        <Grid container alignItems={'center'} spacing={1}>
            {getIcon()}
            <Grid size={'grow'}>{getLabel()}</Grid>
        </Grid>
    )
}

StatusLine.propTypes = {
    isValid: PropTypes.bool,
    validLabel: PropTypes.string,
    invalidLabel: PropTypes.string,
    unknownLabel: PropTypes.string,
}

const ChangeVPLevelDialog = ({ open, onClose, managementUnitName, defaultVpLevel, vpLevelValues = [], estimatedNeed, recommendedVpLevel, linkStationsDetails = [] }) => {
    const [selectedVpLevel, setSelectedVpLevel] = useState(defaultVpLevel)

    useEffect(() => {
        if (open) setSelectedVpLevel(defaultVpLevel)
    }, [open, defaultVpLevel])

    const dispatch = useDispatch()
    const { id: managementUnitCode } = useParams()

    // Niveau de VP que chaque type de ressource peut soutenir (le plus restrictif des stations du type).
    // Une ressource est jugée suffisante pour un niveau de VP donné si elle peut soutenir au moins ce niveau.
    const { piezoStations, hydroStations } = useMemo(() => ({
        piezoStations: linkStationsDetails.filter(({ dataType }) => dataType === DEPTH),
        hydroStations: linkStationsDetails.filter(({ dataType }) => dataType !== DEPTH),
    }), [linkStationsDetails])
    const piezoResourceVpLevel = useMemo(() => getResourceVPLevel(piezoStations), [piezoStations])
    const hydroResourceVpLevel = useMemo(() => getResourceVPLevel(hydroStations), [hydroStations])

    return (
        <Dialog
            open={open}
            maxWidth={'lg'}
        >
            <DefaultDialogTitle title={`${i18n.editVP} : ${managementUnitName}`} onClose={onClose} />
            <DialogContent>
                <Grid container spacing={1}>
                    {range(4).map(vpLevel => {
                        const isNeedsCovered = hasValue(vpLevelValues[vpLevel]) && hasValue(estimatedNeed) ? vpLevelValues[vpLevel] >= estimatedNeed : undefined
                        // Ressource suffisante : elle peut soutenir un niveau de VP au moins égal à celui affiché.
                        const isPiezoResourceSufficient = piezoStations.length && hasValue(piezoResourceVpLevel) ? vpLevel <= piezoResourceVpLevel : undefined
                        const isHydroResourceSufficient = hydroStations.length && hasValue(hydroResourceVpLevel) ? vpLevel <= hydroResourceVpLevel : undefined
                        return (
                            <SummaryIndicator
                                key={vpLevel}
                                size={'grow'}
                                bgColor='white'
                                outlined
                                title={getVPLevelLabel(vpLevel)}
                                onClick={() => setSelectedVpLevel(vpLevel)}
                                content={<Grid size={12}><VPIndicator bigDisplay indicator={vpLevel} volume={vpLevelValues[vpLevel]} /></Grid>}
                                secondaryContent={
                                    <Stack spacing={0.5}>
                                        <StatusLine
                                            isValid={isPiezoResourceSufficient}
                                            validLabel={i18n.sufficientPiezoResource}
                                            invalidLabel={i18n.insufficientPiezoResource}
                                            unknownLabel={i18n.unknownPiezoResourceState}
                                        />
                                        <StatusLine
                                            isValid={isHydroResourceSufficient}
                                            validLabel={i18n.sufficientHydroResource}
                                            invalidLabel={i18n.insufficientHydroResource}
                                            unknownLabel={i18n.unknownHydroResourceState}
                                        />
                                        <StatusLine
                                            isValid={isNeedsCovered}
                                            validLabel={i18n.needsCovered}
                                            invalidLabel={i18n.needsNotCovered}
                                            unknownLabel={i18n.unknownNeedsCoverage}
                                        />
                                    </Stack>
                                }
                                actions={compact([
                                    vpLevel === defaultVpLevel ?
                                        <SmallIndicator key={0} color={PASTEL_COLOR.blue} borderColor={PASTEL_COLOR.blue}>
                                            {i18n.inProgress}
                                        </SmallIndicator> : undefined,
                                    vpLevel === recommendedVpLevel ?
                                        <SmallIndicator key={0} color={PASTEL_COLOR.green} borderColor={PASTEL_COLOR.green}>
                                            {i18n.recommended}
                                        </SmallIndicator> : undefined,
                                    {
                                        icon: vpLevel === selectedVpLevel ? 'radio_button_checked' : 'radio_button_unchecked',
                                        color: blackColor,
                                        onClick: () => setSelectedVpLevel(vpLevel),
                                        size: 'small',
                                    },
                                ])}
                            />
                        )
                    })}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        dispatch(AgriAction.setA3PVPLevel(managementUnitCode, selectedVpLevel)).then(onClose)
                    }}
                    disabled={selectedVpLevel === defaultVpLevel}
                    variant={'contained'}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ChangeVPLevelDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    managementUnitName: PropTypes.string,
    defaultVpLevel: PropTypes.number,
    vpLevelValues: PropTypes.arrayOf(PropTypes.number),
    estimatedNeed: PropTypes.number,
    recommendedVpLevel: PropTypes.number,
    linkStationsDetails: PropTypes.arrayOf(linkStationDetailsShape),
}

export default ChangeVPLevelDialog